/**
 * @fileoverview Onboarding Progress Ring Component
 * 
 * Displays a persistent progress ring in the header showing onboarding completion
 * (e.g., "2/3 steps → 67%"). Collapses once onboarding is complete.
 * 
 * @author Claude Code
 * @since 2024-01-01
 */

import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  X,
  Key, 
  Play, 
  Rocket
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import { useAuth } from '@/lib/auth/useAuth';
import { getApiKeys } from '@/api/api-keys';

interface OnboardingStep {
  id: string;
  title: string;
  completed: boolean;
  icon: React.ReactNode;
}

/**
 * Props for the OnboardingProgressRing component
 */
interface OnboardingProgressRingProps {
  /** Optional CSS class name for custom styling */
  className?: string;
  /** Callback when user dismisses the progress ring */
  onDismiss?: () => void;
}

/**
 * Onboarding Progress Ring Component
 * 
 * Shows completion progress in a compact ring format with:
 * - Circular progress indicator
 * - Step count (e.g., "2/3 steps")
 * - Percentage completion
 * - Dismiss button when complete
 * 
 * @param props - Component props
 * @returns Rendered progress ring component
 */
const OnboardingProgressRing: React.FC<OnboardingProgressRingProps> = ({ 
  className, 
  onDismiss 
}) => {
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const { user } = useAuth();

  /**
   * Initialize and check step completion status
   */
  useEffect(() => {
    const initializeSteps = async () => {
      // Check if user has API keys
      let hasApiKeys = false;
      try {
        if (user?.id) {
          const response = await getApiKeys(user.id);
          hasApiKeys = response.success && response.data && response.data.length > 0;
        }
      } catch (error) {
        console.error('Error checking API keys:', error);
      }

      // Check if user previously dismissed onboarding
      const dismissed = localStorage.getItem('onboarding-dismissed') === 'true';
      setIsDismissed(dismissed);

      const initialSteps: OnboardingStep[] = [
        {
          id: 'api-key',
          title: 'Create API Key',
          completed: hasApiKeys,
          icon: <Key className="h-3 w-3" />
        },
        {
          id: 'run-test',
          title: 'Run First Test',
          completed: false, // Would check if user has any test runs
          icon: <Play className="h-3 w-3" />
        },
        {
          id: 'explore',
          title: 'Explore Dashboard',
          completed: false,
          icon: <Rocket className="h-3 w-3" />
        }
      ];

      setSteps(initialSteps);
      const completed = initialSteps.filter(step => step.completed).length;
      setCompletedCount(completed);
      
      // Auto-hide if all steps are complete or if user dismissed
      if (completed === initialSteps.length || dismissed) {
        setIsVisible(false);
      }
    };

    initializeSteps();
  }, [user?.id]);

  /**
   * Handle dismissing the progress ring
   */
  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('onboarding-dismissed', 'true');
    
    trackEvent('onboarding_progress_dismissed', {
      completed_steps: completedCount,
      total_steps: steps.length,
      completion_percentage: Math.round((completedCount / steps.length) * 100)
    });
    
    onDismiss?.();
  };

  /**
   * Handle clicking on the progress ring to view onboarding
   */
  const handleClick = () => {
    // Scroll to onboarding checklist if it exists
    const element = document.querySelector('[data-component="onboarding-checklist"]');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    
    trackEvent('onboarding_progress_clicked', {
      completed_steps: completedCount,
      total_steps: steps.length
    });
  };

  // Don't render if not visible or dismissed
  if (!isVisible || isDismissed || steps.length === 0) {
    return null;
  }

  const totalSteps = steps.length;
  const progressPercentage = Math.round((completedCount / totalSteps) * 100);
  const isComplete = completedCount === totalSteps;
  
  // Calculate stroke dash array for circular progress
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Circular Progress Ring */}
      <div className="relative">
        <svg 
          width="40" 
          height="40" 
          className="transform -rotate-90"
          onClick={handleClick}
        >
          {/* Background circle */}
          <circle
            cx="20"
            cy="20"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            className="text-gray-200 dark:text-gray-700"
          />
          {/* Progress circle */}
          <circle
            cx="20"
            cy="20"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            className={cn(
              "transition-all duration-300 ease-out",
              isComplete ? "text-green-500" : "text-primary"
            )}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isComplete ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
          ) : (
            <span className="text-xs font-bold text-primary">
              {progressPercentage}%
            </span>
          )}
        </div>
      </div>

      {/* Progress Text */}
      <Button
        variant="ghost"
        size="sm"
        className="h-auto p-1 hover:bg-accent/50"
        onClick={handleClick}
      >
        <div className="text-left">
          <div className="text-xs font-medium">
            {completedCount}/{totalSteps} steps
          </div>
          <div className="text-xs text-muted-foreground">
            Setup progress
          </div>
        </div>
      </Button>

      {/* Dismiss button */}
      {(isComplete || progressPercentage > 75) && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 rounded-full"
          onClick={handleDismiss}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};

export default OnboardingProgressRing;